import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData } from '@/mocks/articles';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: '校园春色',
      url: 'https://qinglanriyu.cn',
      description: '用镜头和文字，记录青春校园里最美好的四季风光与青春故事',
      email: 'hello@qinglanriyu.cn',
      foundingDate: '2023',
      address: {
        '@type': 'PostalAddress',
        addressLocality: '北京',
        addressRegion: '海淀区',
        addressCountry: 'CN',
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qinglanriyu.cn' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://qinglanriyu.cn/about' },
      ],
    },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    if ((formData.get('message') as string || '').length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9tg3rv8l97n3eas39ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormError(parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <SEOHead
        title="关于我们 - 校园春色"
        description="校园春色是一个专注于记录和分享校园美好生活的原创内容平台。了解我们的品牌故事、团队理念，查看读者评价，以及联系方式。"
        keywords="校园春色,关于校园春色,校园摄影平台,青春校园生活,校园记忆分享"
        canonicalUrl="/about"
        ogType="website"
        jsonLd={aboutJsonLd}
      />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        <section className="w-full px-6 md:px-10 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-900">
              {aboutData.brandIntro.title}
            </h1>
            <p className="mt-6 text-foreground-600 leading-relaxed text-sm md:text-base max-w-3xl mx-auto">
              {aboutData.brandIntro.content}
            </p>
            <p className="mt-6 font-heading text-lg md:text-xl text-primary-600 italic">
              "{aboutData.brandIntro.mission}"
            </p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl font-semibold text-foreground-900 text-center mb-12">
              品牌声誉
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              {aboutData.reputation.stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-primary-500">{stat.value}</p>
                  <p className="mt-2 text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-800 text-center mb-8">
              读者评价
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutData.reputation.testimonials.map((t, i) => (
                <div key={i} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed italic">"{t.text}"</p>
                  <p className="mt-4 text-xs text-foreground-400">—— {t.author}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl font-semibold text-foreground-900 text-center mb-4">
              联系我们
            </h2>
            <p className="text-center text-foreground-500 text-sm mb-10">
              如果你有任何建议、投稿意向或合作需求，欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="text-center p-6 rounded-lg border border-background-200/70">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-mail-line text-primary-600 text-xl"></i>
                </div>
                <p className="text-sm font-medium text-foreground-700">电子邮箱</p>
                <p className="mt-1 text-sm text-foreground-500">{aboutData.contact.email}</p>
              </div>
              <div className="text-center p-6 rounded-lg border border-background-200/70">
                <div className="w-12 h-12 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-wechat-line text-accent-600 text-xl"></i>
                </div>
                <p className="text-sm font-medium text-foreground-700">微信公众号</p>
                <p className="mt-1 text-sm text-foreground-500">{aboutData.contact.wechat}</p>
              </div>
              <div className="text-center p-6 rounded-lg border border-background-200/70">
                <div className="w-12 h-12 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-4">
                  <i className="ri-map-pin-line text-secondary-600 text-xl"></i>
                </div>
                <p className="text-sm font-medium text-foreground-700">地址</p>
                <p className="mt-1 text-sm text-foreground-500">{aboutData.contact.address}</p>
              </div>
            </div>

            <form id="about-contact-form" onSubmit={handleSubmit} className="space-y-5" data-readdy-form="">
              <div>
                <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  你的名字 <span className="text-red-400">*</span>
                </label>
                <input
                  id="about-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入你的名字"
                />
              </div>
              <div>
                <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  电子邮箱 <span className="text-red-400">*</span>
                </label>
                <input
                  id="about-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入你的邮箱"
                />
              </div>
              <div>
                <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="about-message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请输入你想说的话（最多500字）"
                ></textarea>
                <p className="mt-1 text-xs text-foreground-400">最多500个字符</p>
              </div>

              <input
                name="company_alt"
                type="text"
                className="form-honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                className="w-full py-3 px-6 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-300"
              >
                发送留言
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-green-600 text-center mt-3">感谢你的留言！我们会尽快回复。</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 text-center mt-3">{formError || '提交失败，请稍后再试'}</p>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
}